import { IPoint } from './plan-primitive';

/** A circular arc between two polygon vertices, in the frame the vertices are in. */
export interface IBulgeArc {
    center: IPoint;
    radius: number;
    startAngle: number;
    endAngle: number;
    /** True when the sweep runs toward decreasing angles (the canvas `anticlockwise` flag). */
    anticlockwise: boolean;
}

/**
 * The bulge of the arc around `center` from `from` to `to`, the short way round:
 * `tan(sweep / 4)`, positive when the sweep turns from +x toward +y of the frame.
 */
export function arcBulge(center: IPoint, from: IPoint, to: IPoint): number {
    const ax: number = from[0] - center[0];
    const ay: number = from[1] - center[1];
    const bx: number = to[0] - center[0];
    const by: number = to[1] - center[1];
    const sweep: number = Math.atan2(ax * by - ay * bx, ax * bx + ay * by);
    return Math.tan(sweep / 4);
}

/**
 * Bulges of an annular sector given as `[innerBack, outerBack, outerFront, innerFront]`:
 * the two radial edges stay straight, the outer and inner edges are arcs around the
 * common centre. The centre is where the radial edges meet, found from the ratio of
 * the inner and outer chords, so no radius has to be passed in.
 */
export function sectorBulges(innerBack: IPoint, outerBack: IPoint, outerFront: IPoint, innerFront: IPoint): readonly number[] {
    const outerChord: number = Math.hypot(outerFront[0] - outerBack[0], outerFront[1] - outerBack[1]);
    const innerChord: number = Math.hypot(innerFront[0] - innerBack[0], innerFront[1] - innerBack[1]);
    if (outerChord === 0 || innerChord >= outerChord) {
        return [0, 0, 0, 0];
    }
    const ratio: number = innerChord / outerChord;
    const center: IPoint = [(innerBack[0] - ratio * outerBack[0]) / (1 - ratio), (innerBack[1] - ratio * outerBack[1]) / (1 - ratio)];
    const outerBulge: number = arcBulge(center, outerBack, outerFront);
    const innerBulge: number = innerChord === 0 ? 0 : arcBulge(center, innerFront, innerBack);
    return [0, outerBulge, 0, innerBulge];
}

/** The arc a bulged segment draws: centre, radius and the canvas sweep from `from` to `to`. */
export function bulgeArc(from: IPoint, to: IPoint, bulge: number): IBulgeArc {
    const dx: number = to[0] - from[0];
    const dy: number = to[1] - from[1];
    const chord: number = Math.hypot(dx, dy);
    // Distance from the chord midpoint to the centre, signed toward the left of the chord.
    const offset: number = ((chord / 2) * (1 - bulge * bulge)) / (2 * bulge);
    const center: IPoint = [(from[0] + to[0]) / 2 - (dy / chord) * offset, (from[1] + to[1]) / 2 + (dx / chord) * offset];
    return {
        center,
        radius: Math.hypot(from[0] - center[0], from[1] - center[1]),
        startAngle: Math.atan2(from[1] - center[1], from[0] - center[0]),
        endAngle: Math.atan2(to[1] - center[1], to[0] - center[0]),
        anticlockwise: bulge < 0,
    };
}
